import * as React from 'react'
import { UserListItem, UserRoleAssignment } from '@/lib/api/company/configuration/adminPanel/users'
import { UpdateUserRolesError } from '@/lib/api/company/configuration/adminPanel/users/useMutationUpdateUserRoles'
import { useQueryCompanyRoles } from '@/lib/api/company/roles'
import { faPlus, faXmark } from '@/lib/fa'
import { FaIcon } from '@/lib/fa'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type Role = { id: number; name: string }

/** `user.roles` ma wpis na każdą firmę użytkownika - edytujemy tylko bieżącą. */
export const companyRolesOf = (user: UserListItem, companyId: string): Role[] =>
  user.roles?.find((assignment) => String(assignment.companyId) === String(companyId))?.roles ?? []

type Props = {
  user: UserListItem
  companyId: string
  onRolesUpdate: (user: UserListItem, roles: UserRoleAssignment[]) => Promise<void>
}

export const RoleManagementPopover: React.FC<Props> = ({ user, companyId, onRolesUpdate }) => {
  const [isOpen, setIsOpen] = React.useState(false)
  const [pendingRoles, setPendingRoles] = React.useState<Role[] | null>(null)
  const [originalRoles, setOriginalRoles] = React.useState<Role[] | null>(null)
  const [isSaving, setIsSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  
  const { t } = useLocalize()
  const rolesQuery = useQueryCompanyRoles(companyId)
  
  const popoverRef = React.useRef<HTMLDivElement>(null)
  const popoverContainerRef = React.useRef<HTMLDivElement>(null)

  // Initialize original roles when popover opens
  React.useEffect(() => {
    if (isOpen) {
      const roles = companyRolesOf(user, companyId)
      setOriginalRoles(roles)
      setPendingRoles(roles)
      setError(null) // Reset error on open
    } else {
      // Reset state when popover closes
      setPendingRoles(null)
      setOriginalRoles(null)
      setError(null)
    }
  }, [isOpen, user, companyId])

  // Close popover when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current && 
        popoverContainerRef.current &&
        !popoverRef.current.contains(event.target as Node) && 
        !popoverContainerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const availableRoles = React.useMemo(() => {
    return rolesQuery.data?.roles.map((role) => ({
      id: Number(role.id),
      name: role.name,
    })) || []
  }, [rolesQuery.data])

  const selectedRoles = pendingRoles ?? companyRolesOf(user, companyId)

  // Check if there are unsaved changes
  const hasUnsavedChanges = React.useMemo(() => {
    if (pendingRoles === null || originalRoles === null) return false
    return JSON.stringify(pendingRoles) !== JSON.stringify(originalRoles)
  }, [pendingRoles, originalRoles])

  const handleAddRole = (roleId: number, roleName: string) => {
    if (selectedRoles.some((r) => r.id === roleId)) return
    setPendingRoles([...selectedRoles, { id: roleId, name: roleName }])
  }

  const handleRemoveRole = (roleId: number) => {
    setPendingRoles(selectedRoles.filter((r) => r.id !== roleId))
  }

  const handleSave = async () => {
    if (pendingRoles !== null) {
      setIsSaving(true)
      setError(null)
      try {
        const current = user.roles?.find((r) => String(r.companyId) === String(companyId))
        await onRolesUpdate(user, [
          {
            companyId: current?.companyId ?? (companyId as unknown as number),
            companyName: current?.companyName ?? '',
            isServiceCompany: current?.isServiceCompany ?? false,
            roles: pendingRoles,
          },
        ])
        setIsOpen(false)
      } catch (err) {
        if (err instanceof UpdateUserRolesError) {
          // Show backend message directly - it contains useful context like role name
          setError(err.message)
        } else if (err instanceof Error) {
          setError(err.message)
        } else {
          setError(t('usersModule.errors.updateFailed'))
        }
      } finally {
        setIsSaving(false)
      }
    }
  }

  const handleClose = () => {
    setPendingRoles(null)
    setIsOpen(false)
  }

  return (
    <>
      <div className="tw-relative tw-inline-block" ref={popoverRef}>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen(!isOpen)
          }}
          className="tw-ml-2 tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary tw-text-white hover:tw-bg-primary/80"
        >
          <FaIcon icon={faPlus} className="tw-h-3 tw-w-3" />
        </button>
      </div>

      {isOpen && (
        <div 
          ref={popoverContainerRef}
          onClick={(e) => e.stopPropagation()}
          className="tw-fixed tw-z-[9999] tw-min-w-[280px] tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-shadow-xl"
          style={{
            top: popoverRef.current?.getBoundingClientRect().bottom || 0,
            left: popoverRef.current?.getBoundingClientRect().left || 0,
          }}
        >
          {/* Selected roles as badges */}
          {selectedRoles.length > 0 && (
            <div className="tw-flex tw-flex-wrap tw-gap-2 tw-border-b tw-border-gray-100 tw-bg-blue-50/50 tw-p-3">
              {selectedRoles.map((role) => (
                <div
                  key={role.id}
                  className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-md tw-bg-blue-100 tw-px-3 tw-py-1"
                >
                  <span className="tw-text-sm tw-text-gray-700">{role.name}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemoveRole(role.id)
                    }}
                    className="tw-text-gray-500 hover:tw-text-gray-700"
                  >
                    <FaIcon icon={faXmark} className="tw-h-3 tw-w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Available roles to add */}
          <div className="tw-max-h-64 tw-overflow-y-auto">
            {availableRoles
              .filter(role => !selectedRoles.some(sr => sr.id === role.id))
              .map((role) => (
                <button
                  key={role.id}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleAddRole(role.id, role.name)
                  }}
                  className="tw-flex tw-w-full tw-items-center tw-justify-between tw-border-b tw-border-gray-50 tw-px-4 tw-py-2.5 tw-text-left tw-text-sm tw-text-gray-700 last:tw-border-b-0 hover:tw-bg-gray-50"
                >
                  <span>{role.name}</span>
                  <FaIcon icon={faPlus} className="tw-h-3 tw-w-3 tw-text-primary" />
                </button>
              ))}
          </div>

          {/* Error message */}
          {error && (
            <div className="tw-border-t tw-border-red-200 tw-bg-red-50 tw-p-3">
              <p className="tw-text-sm tw-text-red-700">{error}</p>
            </div>
          )}

          {/* Save/Close buttons */}
          {hasUnsavedChanges && (
            <div className="tw-border-t tw-border-gray-200 tw-bg-gray-50 tw-p-3">
              <div className="tw-flex tw-gap-2 tw-justify-end">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleClose()
                  }}
                  disabled={isSaving}
                  className="tw-rounded-md tw-border tw-border-gray-300 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 hover:tw-bg-gray-50 disabled:tw-opacity-50"
                >
                  {t('common.close')}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleSave()
                  }}
                  disabled={isSaving}
                  className="tw-rounded-md tw-bg-primary tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary/90 disabled:tw-opacity-50"
                >
                  {isSaving ? t('usersModule.saving') : t('common.save')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

